/**
 * Utilidades para manejo de tokens de Google OAuth
 */
import { jwtDecode } from 'jwt-decode';

/**
 * Valida el formato de un token JWT de Google
 * @param {string} token - Token de Google OAuth
 * @returns {boolean} - True si el token es válido
 */
export const validateGoogleToken = (token) => {
  if (!token || typeof token !== 'string') {
    return false;
  }

  const parts = token.split('.');
  return parts.length === 3;
};

/**
 * Decodifica el payload de un token JWT de Google
 * @param {string} token - Token de Google OAuth
 * @returns {object|null} - Payload decodificado o null si es inválido
 */
export const decodeGoogleToken = (token) => {
  try {
    if (!validateGoogleToken(token)) {
      return null;
    }

    const payload = jwtDecode(token);
    return payload;
  } catch (error) {
    console.error('Error al decodificar token de Google:', error);
    return null;
  }
};

/**
 * Verifica si un token de Google ha expirado
 * @param {string} token - Token de Google OAuth
 * @returns {boolean} - True si el token ha expirado
 */
export const isGoogleTokenExpired = (token) => {
  const payload = decodeGoogleToken(token);
  if (!payload || !payload.exp) {
    return true;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const tolerance = 60; // 60 segundos de tolerancia
  return payload.exp < (currentTime - tolerance);
};

/**
 * Obtiene información del usuario desde el token de Google
 * @param {string} token - Token de Google OAuth
 * @returns {object|null} - Información del usuario o null si es inválido
 */
export const getUserInfoFromGoogleToken = (token) => {
  const payload = decodeGoogleToken(token);
  if (!payload) {
    return null;
  }

  return {
    email: payload.email,
    name: payload.name,
    picture: payload.picture,
    emailVerified: payload.email_verified,
    clientId: payload.aud,
    issuer: payload.iss,
    expiration: payload.exp
  };
};

/**
 * Logs información de debugging del token de Google
 * @param {string} token - Token de Google OAuth
 * @param {string} context - Contexto donde se está usando el token
 */
export const debugGoogleToken = (token, context = 'Unknown') => {
  console.group(`🔍 Google Token Debug - ${context}`);
  
  if (!token) {
    console.error('❌ Token is null or undefined');
    console.groupEnd();
    return;
  }

  const isValid = validateGoogleToken(token);
  const userInfo = getUserInfoFromGoogleToken(token);
  const isExpired = isGoogleTokenExpired(token);

  console.log('✅ Token format valid:', isValid);
  console.log('⏰ Token expired:', isExpired);
  console.log('👤 User info:', userInfo);
  console.log('🔗 Token preview:', token.substring(0, 50) + '...');
  
  console.groupEnd();
};